import {
  DefaultNavigationTabApiResponse,
  NavigationTabApiResponse,
  NavigationTabInput,
} from 'api/navigationTabs'
import { generateUniqueTabId } from 'slices/Cms/Content/AppNavigation/BottomNav/helpers'
import { NavigationTab } from 'slices/Cms/Content/AppNavigation/BottomNav/types'
import { getUrlAddress, getUrlValue } from 'utilities/content/linkUrls'

/**
 * Converts slice navigation tabs to API format for submission
 * @param tabs - Array of navigation tabs from the slice
 * @returns Array of navigation tab inputs for the API
 */
export const convertNavigationTabsToApiFormat = (
  tabs: NavigationTab[]
): NavigationTabInput[] => {
  return tabs.map(tab => {
    const apiTab: NavigationTabInput = {
      id: typeof tab.id === 'string' ? undefined : tab.id, // Only send numeric IDs to API
      name: tab.name,
      link: getUrlAddress(tab.link), // Convert UrlValue to string
      position: tab.position,
      brame_campaign_id: tab.settings?.brame_campaign_id,
      default_icon: tab.default_icon,
      selected_icon: tab.selected_icon,
    }

    // Extract file_upload_id from NavigationTabIcon if present
    const fileUploads: NavigationTabInput['file_uploads'] = {}

    if (tab.default_icon?.file_upload_id) {
      fileUploads.default_icon = {
        file_upload_id: tab.default_icon.file_upload_id,
      }
    }

    if (tab.selected_icon?.file_upload_id) {
      fileUploads.selected_icon = {
        file_upload_id: tab.selected_icon.file_upload_id,
      }
    }

    if (Object.keys(fileUploads).length > 0) {
      apiTab.file_uploads = fileUploads
    }

    return apiTab
  })
}

/**
 * Converts API navigation tabs response to slice format
 * @param apiTabs - Array of navigation tabs from the API
 * @returns Array of navigation tabs for the slice
 */
export const convertNavigationTabsApiToSliceFormat = (
  apiTabs: NavigationTabApiResponse[]
): NavigationTab[] => {
  return apiTabs.map(tab => {
    const sliceTab: NavigationTab = {
      id: tab.id,
      name: tab.name,
      link: getUrlValue(tab.link), // Convert string to UrlValue
      position: tab.position,
      settings: tab.brame_campaign_id
        ? { brame_campaign_id: tab.brame_campaign_id }
        : undefined,
    }

    // Convert API icon format to NavigationTabIcon format
    if (tab.default_icon) {
      sliceTab.default_icon = {
        id: tab.default_icon.id || null,
        url: tab.default_icon.url,
        file_upload_id: tab.default_icon.id, // Use the icon ID as file_upload_id for existing icons
      }
    }

    if (tab.selected_icon) {
      sliceTab.selected_icon = {
        id: tab.selected_icon.id || null,
        url: tab.selected_icon.url,
        file_upload_id: tab.selected_icon.id, // Use the icon ID as file_upload_id for existing icons
      }
    }

    return sliceTab
  })
}

/**
 * Converts API default tabs response to slice format with unique IDs
 * @param apiDefaultTabs - Array of default navigation tabs from the API
 * @param existingTabs - Record of existing tabs to avoid ID conflicts
 * @returns Array of navigation tabs for the slice with unique IDs
 */
export const convertDefaultTabsApiToSliceFormat = (
  apiDefaultTabs: DefaultNavigationTabApiResponse[],
  existingTabs: Record<string, NavigationTab> = {}
): NavigationTab[] => {
  return apiDefaultTabs.map(tab => {
    // Generate a unique ID for default tabs
    const uniqueId = generateUniqueTabId(existingTabs)

    const sliceTab: NavigationTab = {
      id: uniqueId,
      name: tab.name,
      link: getUrlValue(tab.link), // Convert string to UrlValue
      position: tab.position,
      // Default tabs don't have brame campaigns, so no settings needed
    }

    // Convert API icon format to NavigationTabIcon format
    if (tab.default_icon) {
      sliceTab.default_icon = {
        id: tab.default_icon.id || null,
        url: tab.default_icon.url,
        file_upload_id: tab.default_icon.id, // Use the icon ID as file_upload_id for existing icons
      }
    }

    if (tab.selected_icon) {
      sliceTab.selected_icon = {
        id: tab.selected_icon.id || null,
        url: tab.selected_icon.url,
        file_upload_id: tab.selected_icon.id, // Use the icon ID as file_upload_id for existing icons
      }
    }

    // Add the new tab to existingTabs to avoid conflicts in subsequent iterations
    existingTabs[uniqueId] = sliceTab

    return sliceTab
  })
}
